import type { IAgentTool } from '../IAgentTool';
import { TABLE_SCHEMAS } from '../../../../domains/diagram/shapes/table/tableSchemas.registry';
import { ALL_PROVIDER_SHAPE_SPECS } from '../../../../domains/network/providers/providerDialectRegistry';

const GENERIC_TABLE_SCHEMA_IDS = Object.keys(TABLE_SCHEMAS).sort();

const AWS_NETWORK_SHAPE_IDS = [
  'aws_account',
  'aws_region',
  'aws_vpc',
  'aws_subnet',
  'aws_subnet_public',
  'aws_subnet_private',
  'aws_ec2',
  'aws_lambda',
  'aws_ecs',
  'aws_eks',
  'aws_fargate',
  'aws_alb',
  'aws_nlb',
  'aws_api_gateway',
  'aws_alb_listener',
  'aws_target_group',
  'aws_cloudfront',
  'aws_route53',
  'aws_route53_record',
  'aws_acm_cert',
  'aws_nat',
  'aws_igw',
  'aws_route_table',
  'aws_s3',
  'aws_ebs',
  'aws_efs',
  'aws_rds',
  'aws_rds_postgres',
  'aws_rds_mysql',
  'aws_aurora_postgres',
  'aws_aurora_mysql',
  'aws_dynamodb',
  'aws_documentdb',
  'aws_redshift',
  'aws_neptune',
  'aws_opensearch',
  'aws_elasticache_redis',
  'aws_elasticache_memcached',
  'aws_iam_role',
  'aws_iam_user',
  'aws_cognito',
  'aws_sg',
  'aws_kms',
  'aws_waf',
  'aws_secrets_manager',
  'aws_sqs',
  'aws_sns',
  'aws_eventbridge',
  'aws_cloudwatch',
  'aws_cloudwatch_alarm',
  'aws_cloudtrail',
  'aws_autoscaling_group',
  'aws_generic',
];

const BASIC_NETWORK_SHAPE_IDS = [
  'basic_server',
  'basic_laptop',
  'basic_workstation',
  'basic_router',
  'basic_switch',
  'basic_firewall',
  'basic_load_balancer',
  'basic_database',
  'basic_storage',
  'basic_cloud',
  'basic_internet',
  'basic_user',
];

const PROVIDER_NETWORK_SHAPE_IDS = ALL_PROVIDER_SHAPE_SPECS.map((shape) => shape.id);
const NETWORK_SHAPE_IDS = Array.from(
  new Set([...AWS_NETWORK_SHAPE_IDS, ...PROVIDER_NETWORK_SHAPE_IDS, ...BASIC_NETWORK_SHAPE_IDS]),
).sort();

/**
 * RDM Schema Tool
 *
 * Returns the valid fields, relationships, and syntax examples for a given
 * RDM domain type. Agents use this to generate correct RDM without guessing
 * field names or relationship syntax.
 */

const DOMAIN_SCHEMAS: Record<string, DomainSchema> = {
  genogram: {
    domain: 'genogram',
    description: 'Family relationship diagrams for clinical, genealogical, or social contexts.',
    nodeTypes: {
      person: {
        fields: [
          { name: 'sex', type: 'enum', values: ['male', 'female'], required: true },
          { name: 'born', type: 'date', description: 'Birth date (YYYY-MM-DD)' },
          { name: 'died', type: 'date', description: 'Death date (YYYY-MM-DD)' },
          { name: 'status', type: 'enum', values: ['living', 'deceased'] },
          { name: 'health', type: 'array', description: 'List of health conditions' },
          { name: 'occupation', type: 'string' },
          { name: 'education', type: 'string' },
          { name: 'causeOfDeath', type: 'string' },
          { name: 'adopted', type: 'boolean' },
          { name: 'twin', type: 'enum', values: ['identical', 'fraternal'] },
        ],
      },
    },
    relationships: [
      { syntax: 'married person1 -- person2 { since: YYYY }', description: 'Marriage relationship' },
      { syntax: 'divorced person1 -- person2 { since: YYYY }', description: 'Divorced relationship' },
      { syntax: 'child parent1, parent2 -> child_id', description: 'Parent-child (two parents)' },
      { syntax: 'child parent1 -> child_id', description: 'Parent-child (single parent)' },
      { syntax: 'twins monozygotic parent1, parent2 -> twin1, twin2', description: 'Identical twins' },
      { syntax: 'twins dizygotic parent1, parent2 -> twin1, twin2', description: 'Fraternal twins' },
    ],
    emotionalRelationships: ['close', 'conflict', 'cutoff', 'distant', 'enmeshed'],
    structure: 'genogram Name {\n  generation N {\n    person id "Name" { ... }\n    married p1 -- p2 { ... }\n    child p1, p2 -> child_id\n  }\n}',
    example: `---
type: genogram
title: "Sample Family"
---

genogram SampleFamily {
  generation 1 {
    person gf "John" { sex: male, born: 1940-01-15 }
    person gm "Mary" { sex: female, born: 1942-03-20 }
    married gf -- gm { since: 1962 }
  }
  generation 2 {
    person dad "Bob" { sex: male, born: 1965-06-10 }
    child gf, gm -> dad
  }
}`,
  },
  orgchart: {
    domain: 'orgchart',
    description: 'Organizational hierarchy diagrams showing reporting relationships and team structure.',
    nodeTypes: {
      position: {
        fields: [
          { name: 'title', type: 'string', description: 'Job title', required: true },
          { name: 'department', type: 'string', description: 'Department or division' },
          { name: 'level', type: 'number', description: 'Hierarchy level (1 = top)' },
          { name: 'reportsTo', type: 'reference', description: 'ID of the position this reports to' },
          { name: 'location', type: 'string', description: 'Office or region' },
          { name: 'status', type: 'enum', values: ['active', 'vacant', 'planned'] },
        ],
      },
    },
    relationships: [
      { syntax: 'reportsTo: parent_id', description: 'Direct reporting relationship (inside position properties)' },
      { syntax: 'team name under parent_id { ... }', description: 'Team grouping under a position' },
    ],
    emotionalRelationships: [],
    structure: 'org Name {\n  position id "Display Name" {\n    title: "...", department: "...", level: N\n  }\n  team name under parent_id {\n    position id2 "Name" { ... }\n  }\n}',
    example: `---
type: orgchart
title: "Engineering Team"
---

org Engineering {
  position ceo "Alice Chen" {
    title: "CEO"
    department: "Executive"
    level: 1
  }
  position vp_eng "Bob Smith" {
    title: "VP Engineering"
    department: "Engineering"
    level: 2
    reportsTo: ceo
  }
  team frontend under vp_eng {
    position lead_fe "Carol Davis" {
      title: "Frontend Lead"
      department: "Engineering"
      level: 3
    }
  }
}`,
  },
  process: {
    domain: 'process',
    description: 'Business process and workflow diagrams showing task sequences and flows.',
    nodeTypes: {
      task: {
        fields: [
          { name: 'label', type: 'string', description: 'Display text inside the property block for a bare node identifier.' },
          {
            name: 'type',
            type: 'enum',
            values: ['task', 'start', 'end', 'decision', 'gateway', 'input-output', 'document', 'database', 'delay'],
            required: true,
            description:
              'Shape of the step. Use the full vocabulary, not just task: start/end for the entry and exit points, ' +
              'decision (or gateway) for any yes/no or multi-way branch, input-output for data entering or leaving ' +
              '(also accepts data/io/input/output), document for a generated document/report, database for a data ' +
              'store, delay for a wait. task is a plain activity box.',
          },
          { name: 'assignee', type: 'string', description: 'Role or person responsible' },
          { name: 'duration', type: 'number', description: 'Expected duration in days' },
          { name: 'sla', type: 'number', description: 'SLA deadline in days' },
          { name: 'priority', type: 'enum', values: ['low', 'medium', 'high', 'critical'] },
        ],
      },
    },
    relationships: [
      { syntax: 'task1 -> task2', description: 'Sequential flow between tasks' },
      { syntax: 'task1 -> task2\ntask1 -> task3', description: 'Parallel split (one task to multiple)' },
      { syntax: 'task1 -> task2\ntask2 -> task3', description: 'Chained flow' },
      { syntax: 'decision1 -> task2 { label: "Yes" }', description: 'Labeled branch out of a decision — always label each outgoing edge of a decision' },
    ],
    emotionalRelationships: [],
    structure: 'process Name {\n  start_id { label: "Start", type: start }\n  task_id { label: "Display Name", type: task, assignee: "Role" }\n  check_id { label: "OK?", type: decision }\n  end_id { label: "Done", type: end }\n  rejected { label: "Rejected", type: end }\n  start_id -> task_id\n  task_id -> check_id\n  check_id -> end_id { label: "Yes" }\n  check_id -> rejected { label: "No" }\n}',
    example: `---
type: process
title: "Order Fulfillment"
---

process OrderFulfillment {
  receive_order { label: "Order Received", type: start }
  validate_order { label: "Validate Order", type: task, assignee: "Sales" }
  in_stock { label: "In Stock?", type: decision }
  backorder { label: "Backorder Item", type: task, assignee: "Warehouse" }
  process_payment { label: "Process Payment", type: task }
  payment_ok { label: "Payment Approved?", type: decision }
  pack_order { label: "Pick & Pack", type: task, assignee: "Warehouse" }
  invoice { label: "Generate Invoice", type: document }
  notify { label: "Send Notification", type: input-output }
  order_complete { label: "Order Shipped", type: end }
  cancelled { label: "Order Cancelled", type: end }

  receive_order -> validate_order
  validate_order -> in_stock
  in_stock -> process_payment { label: "Yes" }
  in_stock -> backorder { label: "No" }
  backorder -> in_stock
  process_payment -> payment_ok
  payment_ok -> pack_order { label: "Approved" }
  payment_ok -> cancelled { label: "Declined" }
  pack_order -> invoice
  invoice -> notify
  notify -> order_complete
}`,
  },
  table: {
    domain: 'table',
    description: 'Structured table/grid diagrams such as SWOT, kanban boards, retros, and schema-backed matrices.',
    nodeTypes: {
      table: {
        fields: [
          { name: 'layout', type: 'enum', values: ['stack', 'grid', 'free'], description: 'Default layout algorithm for cell content' },
          { name: 'schemaId', type: 'enum', values: GENERIC_TABLE_SCHEMA_IDS, description: 'Optional explicit built-in schema id when the table id differs from the schema id' },
          { name: 'columns', type: 'array', description: 'Custom column labels for non-schema tables' },
          { name: 'rows', type: 'array', description: 'Optional custom row labels for non-schema tables' },
          { name: 'sort', type: 'string', description: 'Optional property to sort items within a cell' },
          { name: 'order', type: 'enum', values: ['asc', 'desc'], description: 'Sort direction when `sort` is used' },
        ],
      },
      sticky: {
        fields: [
          { name: 'cell', type: 'reference', description: 'Cell label or [row, col] content-space coordinate', required: true },
          { name: 'pos', type: 'array', description: 'Optional [x, y] offset inside the cell when table layout is `free`' },
        ],
      },
      card: {
        fields: [
          { name: 'cell', type: 'reference', description: 'Cell label or [row, col] content-space coordinate', required: true },
          { name: 'assignee', type: 'string', description: 'Optional owner/assignee label' },
          { name: 'priority', type: 'string', description: 'Optional priority field for sorting' },
        ],
      },
      text: {
        fields: [
          { name: 'cell', type: 'reference', description: 'Cell label or [row, col] content-space coordinate', required: true },
          { name: 'pos', type: 'array', description: 'Optional [x, y] offset inside the cell when table layout is `free`' },
        ],
      },
      node: {
        fields: [
          { name: 'cell', type: 'reference', description: 'Cell label or [row, col] content-space coordinate', required: true },
        ],
      },
    },
    relationships: [],
    emotionalRelationships: [],
    structure: 'table swot "SWOT Analysis" { layout: stack }\nsticky s1 "Strong brand recognition" { cell: "Strengths" }',
    example: `@type table
@title "Product SWOT"

table swot "SWOT Analysis" { layout: stack }
sticky s1 "Strong brand recognition" { cell: "Strengths" }
sticky s2 "Limited marketing budget" { cell: "Weaknesses" }
sticky s3 "Emerging markets" { cell: "Opportunities" }
sticky s4 "Aggressive competitors" { cell: "Threats" }`,
  },
  network: {
    domain: 'network',
    description: 'Cloud, Kubernetes, hybrid, and provider-neutral network architecture diagrams.',
    nodeTypes: {
      resource: {
        fields: [
          { name: 'type', type: 'enum', values: NETWORK_SHAPE_IDS, description: 'Canvas shape manifest id, e.g. aws_ec2, azure_vm, gcp_cloud_run, k8s_deployment, generic_compute.', required: true },
          { name: 'resourceId', type: 'string', description: 'Provider-native ARN, Azure resource id, GCP self link, Kubernetes UID, or imported source id.' },
          { name: 'region', type: 'string', description: 'Region, location, or cluster region.' },
          { name: 'az', type: 'string', description: 'Availability zone or zone.' },
          { name: 'accountId', type: 'string', description: 'Cloud account, subscription, project, or tenant identifier.' },
          { name: 'environment', type: 'string', description: 'Environment label such as prod, staging, dev, shared.' },
          { name: 'cidr', type: 'string', description: 'CIDR block for networks and subnets.' },
          { name: 'encrypted', type: 'boolean', description: 'Whether storage/data is encrypted.' },
          { name: 'publicExposure', type: 'enum', values: ['none', 'restricted', 'open'], description: 'Public reachability assessment.' },
          { name: 'tags', type: 'object', description: 'Provider tags/labels.' },
          { name: 'stack', type: 'object', description: 'Optional stack summary, e.g. { count: 3 } for repeated resources.' },
        ],
      },
      cluster: {
        fields: [
          { name: 'label', type: 'string', description: 'Visual grouping container. Use for account, region, VPC/network, namespace, or tier grouping when resource nodes need nesting.' },
        ],
      },
    },
    relationships: [
      { syntax: 'child --contains--> parent', description: 'Containment/nesting. Use leaf -> subnet/network -> account/region direction.' },
      { syntax: 'client --routing--> edge_or_service', description: 'Traffic routing such as Internet -> CDN -> ALB/API gateway -> service.' },
      { syntax: 'compute --sg_access--> datastore', description: 'Security-group or network-policy reachability.' },
      { syntax: 'service_a --data_flow--> service_b', description: 'Application data movement or async event flow.' },
      { syntax: 'workload --identity--> role_or_service_account', description: 'IAM role, managed identity, service account, or workload identity attachment.' },
      { syntax: 'monitor --governance--> resource', description: 'Logging, monitoring, audit, backup, or policy relationship.' },
      { syntax: 'source --network_access--> target', description: 'Generic network access when the more specific kind is unknown.' },
    ],
    emotionalRelationships: [],
    structure: 'network Name {\n  resource id "Display Name" { type: aws_ec2, region: "us-east-1" }\n  child --contains--> parent\n  source --routing--> target\n}',
    example: `---
type: network
title: "Checkout Platform"
---

network CheckoutPlatform {
  resource account "Production account" { type: aws_account, accountId: "123456789012", environment: "prod" }
  resource region "us-east-1" { type: aws_region, region: "us-east-1" }
  resource vpc "checkout-vpc" { type: aws_vpc, cidr: "10.20.0.0/16", environment: "prod" }
  resource public_a "Public subnet A" { type: aws_subnet_public, cidr: "10.20.1.0/24", az: "us-east-1a" }
  resource private_a "Private subnet A" { type: aws_subnet_private, cidr: "10.20.11.0/24", az: "us-east-1a" }
  resource cdn "CloudFront edge" { type: aws_cloudfront, publicExposure: restricted }
  resource alb "Checkout ALB" { type: aws_alb, publicExposure: restricted }
  resource api "Checkout service" { type: aws_ecs, stack: { count: 3 } }
  resource db "Orders database" { type: aws_rds_postgres, encrypted: true, publicExposure: none }
  resource monitor "CloudWatch" { type: aws_cloudwatch }

  vpc --contains--> region
  public_a --contains--> vpc
  private_a --contains--> vpc
  alb --contains--> public_a
  api --contains--> private_a
  db --contains--> private_a
  cdn --routing--> alb
  alb --routing--> api
  api --sg_access--> db
  monitor --governance--> api
  monitor --governance--> db
}`,
  },
  generic: {
    domain: 'generic',
    description: 'Generic diagrams for canvas-native shapes that do not fit a domain-specific RDM block.',
    nodeTypes: {
      node: {
        fields: [
          { name: 'type', type: 'enum', values: ['table'], required: true },
          { name: 'schemaId', type: 'enum', values: GENERIC_TABLE_SCHEMA_IDS, description: 'Built-in table preset to instantiate' },
          { name: 'rowCount', type: 'number', description: 'Optional table row count override' },
          { name: 'columnCount', type: 'number', description: 'Optional table column count override' },
          { name: 'cellText', type: 'object', description: 'Sparse cell text map keyed by "row,col"' },
          { name: 'mergedRegions', type: 'array', description: 'Optional merged cell regions' },
        ],
      },
    },
    relationships: [
      { syntax: 'node_id -> node_id', description: 'Generic connector between nodes' },
    ],
    emotionalRelationships: [],
    structure: 'diagram Name {\n  node id "Label" { type: table, schemaId: "raci" }\n}',
    example: `---
type: generic
title: "RACI Matrix"
---

diagram TeamRaci {
  node raci "RACI Matrix" {
    type: table
    schemaId: "raci"
  }
}`,
  },
};

interface FieldDef {
  name: string;
  type: string;
  values?: string[];
  description?: string;
  required?: boolean;
}

interface DomainSchema {
  domain: string;
  description: string;
  nodeTypes: Record<string, { fields: FieldDef[] }>;
  relationships: Array<{ syntax: string; description: string }>;
  emotionalRelationships: string[];
  structure: string;
  example: string;
}

export const rdmSchemaTool: IAgentTool = {
  name: 'rdm_schema',
  description:
    'Get the RDM schema for a diagram domain (genogram, orgchart, process, table, network). ' +
    'Also supports generic diagram syntax for schema-backed tables. ' +
    'Returns valid node types, fields, relationships, and syntax examples. ' +
    'Use this before generating RDM to ensure correct field names and syntax.',
  parameters: {
    type: 'object',
    properties: {
      provider: { type: 'string', enum: ['aws', 'azure', 'gcp', 'kubernetes', 'generic', 'basic'], description: 'Network dialect filter; omit for mixed-provider search.' },
      query: { type: 'string', description: 'Network registered type ID or service search terms.' },
      offset: { type: 'integer', minimum: 0, description: 'Next catalog page offset.' },
      limit: { type: 'integer', minimum: 1, maximum: 64, description: 'Network catalog page size (default 32).' },
      domain: {
        type: 'string',
        enum: ['genogram', 'orgchart', 'process', 'table', 'network', 'generic'],
        description: 'The diagram domain to get schema for.',
      },
    },
    required: ['domain'],
  },
  supportedDiagramTypes: [],
  tier: 'safe',
  category: 'query',

  async execute(params) {
    const domain = String(params.domain ?? '').toLowerCase();
    const schema = DOMAIN_SCHEMAS[domain];

    if (!schema) {
      return {
        success: false,
        summary: `Unknown domain "${domain}". Valid domains: ${Object.keys(DOMAIN_SCHEMAS).join(', ')}`,
      };
    }

    if (domain === 'network') {
      const provider = typeof params.provider === 'string' ? params.provider : undefined;
      const prefixes: Record<string, string> = { aws: 'aws_', azure: 'azure_', gcp: 'gcp_', kubernetes: 'k8s_', generic: 'generic_', basic: 'basic_' };
      if (provider && !prefixes[provider]) return { success: false, summary: 'Unknown network provider.' };
      const query = typeof params.query === 'string' ? params.query.trim().toLowerCase() : '';
      const terms = query.split(/\s+/).filter(Boolean);
      const dialects = new Map(ALL_PROVIDER_SHAPE_SPECS.map(shape => [shape.id, shape.dialectId]));
      const descriptions = new Map(ALL_PROVIDER_SHAPE_SPECS.map(shape => [shape.id, `${shape.id} ${shape.label} ${shape.category} ${shape.semanticType}`.toLowerCase()]));
      const matching = NETWORK_SHAPE_IDS.filter(id => (!provider || dialects.get(id) === provider || (!dialects.has(id) && id.startsWith(prefixes[provider]))) && terms.every(term => (descriptions.get(id) ?? id).includes(term)));
      const offset = typeof params.offset === 'number' && Number.isInteger(params.offset) ? Math.max(0, params.offset) : 0;
      const limit = typeof params.limit === 'number' && Number.isInteger(params.limit) ? Math.min(64, Math.max(1, params.limit)) : 32;
      const values = matching.slice(offset, offset + limit);
      const nodeTypes = Object.fromEntries(Object.entries(schema.nodeTypes).map(([name, node]) => [name, { fields: node.fields.map(field => field.name === 'type' ? { ...field, values } : field) }]));
      return {
        success: true,
        summary: `Network schema: ${values.length} of ${matching.length} matching registered types. Catalog page only; retrieve further pages when needed.`,
        data: { ...schema, nodeTypes, catalog: { provider, query, offset, limit, total: matching.length, partial: values.length < matching.length, nextOffset: offset + values.length < matching.length ? offset + values.length : null } },
      };
    }

    return {
      success: true,
      summary: `Schema for ${domain}: ${Object.keys(schema.nodeTypes).length} node type(s), ${schema.relationships.length} relationship type(s).`,
      data: schema as unknown as Record<string, unknown>,
    };
  },
};
